import type { RoleEnumTypeV2 } from '@/features/auth/types'
import type { FileType, FolderType } from '@/features/files/types'
import type { V2UserDataType } from '@/features/users/types'

export enum SessionStatusEnumType {
  PLANNED = 'PLANNED',
  WAIT_REVIEW = 'WAIT_REVIEWS',
  WAIT_CONFIRMATION = 'WAIT_CONFIRMATION',
  FINISHED = 'FINISHED',
  CANCELLED = 'CANCELLED',
  NONE = 'NONE', // none for admin who does not have a session
  ONGOING = 'ONGOING', // ongoing for admin view only
}

export type SessionLocationType = 'visio' | string

export type V1SessionType = {
  id: string
  at: string | null
  from: string
  to: string
  name: string
  location: SessionLocationType
  duration: number
  mentoringGroupId: string
  createdBy?: string | null
  createdAt: string
  updatedAt: string
  users: {
    email: string
    id: string
    identity: {
      firstname: string
      lastname: string
    }
    personalInformation?: {
      profilPicture: string | null
    } | null
  }[]
  mentorshipGroup: {
    name: string
    customName: string | null
    conversationId: string
    createdAt: string
    id: string
    idByMentor: number
    meetingRoomId: string
    programId: string
    updatedAt: string
    groupUsers: {
      userId: string
      roles: RoleEnumTypeV2[]
      user: {
        id: string
        email: string
        identity: {
          firstname: string
          lastname: string
        }
        personalInformation: {
          profilPicture: string | null
        }
      }
    }[]
  }
  presences: {
    userId: string
    isPresent: boolean
    user: {
      id: string
      email: string
      password: string | null
      tokenForgotPassword: string | null
      createdAt: string
      updatedAt: string
      samlId: string | null
    }
  }[]
  deliverables: {
    id: string
    userId: string
    sessionId: string
    folderId: string
    createdAt: string
    updatedAt: string
    rating: number | null
  }[]
  statuses: { status: SessionStatusEnumType; userId: string }[]
  report: {
    comment: string
    privateComment: string | null
    userId: string
    createdAt: string
    updatedAt: string
  }
  menteeReports?: {
    comment: string
    userId: string
    createdAt: string
    updatedAt: string
    user?: {
      id: string
      email: string
      identity?: {
        firstname: string
        lastname: string
      }
    }
  }[]
  reviews: {
    grade: number
    review: string
    userId: string
  }[]
  objectives?: {
    id: string
    title: string
    order: number
    mentorReportTemplate?: string | null
    menteeReportTemplate?: string | null
  }[]
}

export type V2SessionType = {
  id: string
  at: string | null
  from: string
  to: string
  name: string
  location: SessionLocationType
  duration: number
  mentoringGroupId: string
  // personne qui a reserve la session (nul pour les sessions anterieures)
  createdBy?: string | null
  mentoringGroupName: string
  meetingRoomId: string
  createdAt: string
  updatedAt: string
  status: SessionStatusEnumType
  users: (Omit<V2UserDataType, 'role'> & {
    sessionStatus: SessionStatusEnumType
    roles: RoleEnumTypeV2[]
  })[]
  report: {
    comment: string
    privateComment: string | null
    userId: string
    createdAt: string
    updatedAt: string
  } | null
  menteeReports?: {
    comment: string
    userId: string
    createdAt: string
    updatedAt: string
    user?: {
      id: string
      email: string
      identity?: {
        firstname: string
        lastname: string
      }
    }
  }[]
  deliverables: {
    id: string
    userId: string
    sessionId: string
    folderId: string
    createdAt: string
    updatedAt: string
    rating: number | null
  }[]
  reviews: {
    grade: number
    review: string
    userId: string
  }[]
  presences: {
    userId: string
    wasPresent: boolean
  }[]
  objective?: {
    id: string
    title: string
    order: number
    mentorReportTemplate?: string | null
    menteeReportTemplate?: string | null
  } | null
}
export type GetSessionsDTOType = {
  programId: string
  periodId: string
  page: number
  perPage: number
  filterEmail: string
  groupId?: string
  filterStatus?: string
}

export type GetSessionDTOType = {
  id: string
}

export type GetSessionsResponseType = {
  sessions: V1SessionType[]
  totalSessions: number
  totalPages: number
}

export type GetSessionResponseType = {
  session: V1SessionType
}

export type ReviewSessionDTOType = {
  sessionId: string
  rating?: number
  comment?: string
}

export type V1AdminExportSessionsDTOType = {
  programId: string
  periodId: string
}

export type V1AdminExportSessionsResponseType = {
  mentee1DelivrableRating: string
  mentee1DelivrableUrl: string
  mentee1Email: string
  mentee1FirstName: string
  mentee1LastName: string
  mentee1Presence: string
  mentee1ReviewComment: string
  mentee2DelivrableRating: string
  mentee2DelivrableUrl: string
  mentee2Email: string
  mentee2FirstName: string
  mentee2LastName: string
  mentee2Presence: string
  mentee2ReviewComment: string
  // menteeXDeliverable...
  // menteeXEmail...
  mentorDelivrableReport: string
  sessionDate: string
  mentorEmail: string
  mentorFirstName: string
  mentorLastName: string
  sessionNumber: string
  totalDeliverablesRating: string
}[]

export type V1CreateSessionDTOType = {
  userId: string
  from: string
  to: string
  mentoringGroupId: string
  location: string
}

export type V1CreateSessionResponseType = {
  message: string
  session: V1SessionType
}

export type V2SessionDetailsType = V2SessionType & object

export type V1UpdateSessionStatusDTOType = {
  sessionId: string
  status: SessionStatusEnumType
}

export type V1ReviewSessionDTOType = {
  userId: string
  grade: number
  review?: string
  sessionId: string
}

export type GetDeliverablePresignedURLDTOType = {
  sessionId: string
  name: string
  userId: string
  mimetype: string
}

type GetDeliverableResponseType = {
  id: string
  rating: number | null
  skipDeliverable?: boolean
  sessionId: string
  userId: string
  folder: FolderType
}

export type GetDeliverableByUserIdResponseType = GetDeliverableResponseType
export type DeliverableType = {
  id: string
  userId: string
  folderId: string
  createdAt: string
  updatedAt: string
  rating: number | null
  skipDeliverable?: boolean
  folder: FolderType | null
}

export type GetDeliverablePresignedURLResponseType = FileType & {
  url: string
}

export type GetAllDeliverablesDTOType = {
  sessionId: string
}

export type GetDeliverableByUserIdDTOType = {
  sessionId: string
  userId: string
}

export type GetAllDeliverablesResponseType = GetDeliverableResponseType[]

export type CheckFileImportationDTOType = {
  fileId: string
}

export type CheckDeliverableImportationResponseType = {
  isImported: boolean
}

export type DeleteFileDTOType = {
  fileId: string
}

export type DeleteFileResponseType = {
  message: string
}

export type V1AddDeliverableRateDTOType = {
  userId: string
  sessionId: string
  rate: number
}

export enum DeliverablesNotationPossibleStatusType {
  RATED = 'RATED',
  NOT_RATED = 'NOT_RATED',
}

export type V1ConfirmPresenceDTOType = {
  sessionId: string
  presences: {
    userId: string
    isPresent: boolean
  }[]
}

export type V1AddSessionReportDTOType = {
  sessionId: string
  comment: string
}

export type V1AddSessionMenteeReportDTOType = {
  sessionId: string
  comment: string
}
